import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DELIVERY_WIDGET_BODY_HTML } from './deliveryWidgetMarkup';
import { useCompanyWidgetConfig } from '../hooks/useCompanyWidgetConfig';

export const WIDGET_COMPLETE_MESSAGE_TYPE = 'filuetship.widget.complete';

const WIDGET_VERSION = '0.1.9';
const WIDGET_CDN_BASE = `https://cdn.jsdelivr.net/npm/@filuet/widget@${WIDGET_VERSION}`;
const WIDGET_SCRIPT_URL = `${WIDGET_CDN_BASE}/dist/widget.iife.js`;
const WIDGET_STYLE_URL = `${WIDGET_CDN_BASE}/dist/widget.css`;
const FLAG_ICONS_STYLE_URL =
  'https://cdn.jsdelivr.net/npm/flag-icons@7.5.0/css/flag-icons.min.css';
const LIBPHONENUMBER_SCRIPT_URL =
  'https://cdn.jsdelivr.net/npm/libphonenumber-js@1.11.18/bundle/libphonenumber-max.js';

function resolveServiceUrl(envValue, configuredValue) {
  // Prefer console env (local proxy / docker) over company widgetConfig URLs,
  // which often point at another host and break local debugging.
  const env = String(envValue || '').trim();
  const configured = String(configuredValue || '').trim();
  const raw = env || configured;
  if (!raw) {
    return window.location.origin.replace(/\/+$/, '');
  }
  try {
    // Always absolutize: widget iframe uses <base href="cdn.jsdelivr.net/...">,
    // so relative /api/... paths would otherwise resolve to the CDN.
    return new URL(raw, window.location.origin).toString().replace(/\/+$/, '');
  } catch {
    return window.location.origin.replace(/\/+$/, '');
  }
}

function normalizeLocale(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (raw.startsWith('ru')) return 'ru';
  if (raw.startsWith('en')) return 'en';
  return 'en';
}

function buildWidgetConfig(companyWidgetConfig, runtime) {
  const base = companyWidgetConfig && typeof companyWidgetConfig === 'object' ? companyWidgetConfig : {};

  return {
    ...base,
    deliveryCoreUrl: resolveServiceUrl(
      process.env.REACT_APP_DELIVERY_CORE_API_URL,
      base.deliveryCoreUrl
    ),
    authIdentityUrl: resolveServiceUrl(
      process.env.REACT_APP_AUTH_IDENTITY_API_URL,
      base.authIdentityUrl
    ),
    clientCoreUrl: resolveServiceUrl(
      process.env.REACT_APP_CLIENT_CORE_API_URL,
      base.clientCoreUrl
    ),
    apiBaseUrl: resolveServiceUrl(process.env.REACT_APP_API_BASE_URL, base.apiBaseUrl),
    apiKey: String(base.apiKey || '').trim(),
    googleMapsApiKey: String(base.googleMapsApiKey || '').trim(),
    companyId: String(runtime.companyId || base.companyId || '').trim(),
    customerId: String(runtime.customerId || base.customerId || '').trim(),
    calculationPayloadId: String(runtime.calculationPayloadId || '').trim(),
    locale: normalizeLocale(runtime.locale || base.locale),
    environment: String(runtime.environment || base.environment || 'TEST').trim() || 'TEST',
    maxWidth: base.maxWidth ?? 640,
    refreshIntervalMinutes: base.refreshIntervalMinutes ?? 5,
    defaultDeliveryType: base.defaultDeliveryType || 'pickup',
    allowExternalPhoneNumber: base.allowExternalPhoneNumber ?? false,
    themeColor: base.themeColor,
    recipientFirstName: String(
      runtime.recipientFirstName ?? base.recipientFirstName ?? base.recipient_first_name ?? ''
    ).trim(),
    recipientLastName: String(
      runtime.recipientLastName ?? base.recipientLastName ?? base.recipient_last_name ?? ''
    ).trim(),
    recipientEmail: String(
      runtime.recipientEmail ?? base.recipientEmail ?? base.recipient_email ?? ''
    ).trim(),
    recipientPhone: String(
      runtime.recipientPhone ?? base.recipientPhone ?? base.recipient_phone ?? ''
    ).trim(),
  };
}

function buildWidgetSrcdoc(config) {
  const configJson = JSON.stringify(config).replace(/</g, '\\u003c');
  return `<!DOCTYPE html>
<html lang="${config.locale || 'en'}">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <base href="${WIDGET_CDN_BASE}/" />
  <link rel="stylesheet" href="${WIDGET_STYLE_URL}" />
  <link rel="stylesheet" href="${FLAG_ICONS_STYLE_URL}" />
  <script>window.FILUETSHIP_WIDGET_CONFIG = ${configJson};<\/script>
</head>
<body>
${DELIVERY_WIDGET_BODY_HTML}
<script src="${LIBPHONENUMBER_SCRIPT_URL}"><\/script>
<script src="${WIDGET_SCRIPT_URL}"><\/script>
</body>
</html>`;
}

export default function DeliveryWidgetEmbed({
  calculationPayloadId,
  companyId,
  customerId,
  environment,
  locale,
  recipientFirstName,
  recipientLastName,
  recipientEmail,
  recipientPhone,
  onComplete,
}) {
  const { t } = useTranslation();
  const { widgetConfig, loading, error } = useCompanyWidgetConfig(companyId);

  useEffect(() => {
    if (!onComplete) {
      return undefined;
    }

    const expectedPayloadId = String(calculationPayloadId || '').trim();
    if (!expectedPayloadId) {
      return undefined;
    }

    const handleMessage = (event) => {
      const data = event?.data;
      if (!data || data.type !== WIDGET_COMPLETE_MESSAGE_TYPE) {
        return;
      }
      const payloadId = String(data.calculationPayloadId || '').trim();
      if (!payloadId || payloadId !== expectedPayloadId) {
        return;
      }
      onComplete({
        calculationPayloadId: payloadId,
        confirmed: data.confirmed !== false,
      });
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onComplete, calculationPayloadId]);

  const config = useMemo(
    () =>
      buildWidgetConfig(widgetConfig, {
        calculationPayloadId,
        companyId,
        customerId,
        environment,
        locale,
        recipientFirstName,
        recipientLastName,
        recipientEmail,
        recipientPhone,
      }),
    [
      widgetConfig,
      calculationPayloadId,
      companyId,
      customerId,
      environment,
      locale,
      recipientFirstName,
      recipientLastName,
      recipientEmail,
      recipientPhone,
    ]
  );

  const srcdoc = useMemo(() => (widgetConfig ? buildWidgetSrcdoc(config) : ''), [widgetConfig, config]);

  if (!calculationPayloadId) {
    return <div className="webhooks-muted">{t('ordersCreateWidgetMissingPayload')}</div>;
  }

  if (loading) {
    return <div className="webhooks-muted">{t('loading')}…</div>;
  }

  if (error) {
    return <div className="webhooks-error">{t(error)}</div>;
  }

  if (!config.apiKey) {
    return <div className="webhooks-error">{t('ordersCreateWidgetMissingApiKey')}</div>;
  }

  return (
    <iframe
      className="delivery-widget-embed"
      title={t('ordersCreateWidgetTitle')}
      srcDoc={srcdoc}
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
    />
  );
}
